import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { LazyLoadImage } from "react-lazy-load-image-component";

const images = [
  "/images/AboutUs/AboutSprigstack/Frame1.png",
  "/images/AboutUs/AboutSprigstack/Frame2.png",
  "/images/AboutUs/AboutSprigstack/Frame3.png",
  "/images/AboutUs/AboutSprigstack/Frame4.png",
  "/images/AboutUs/AboutSprigstack/Frame5.png",
  "/images/AboutUs/AboutSprigstack/Frame6.png",
  "/images/AboutUs/AboutSprigstack/Frame7.png",
];

const AboutSprigstack = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.3, once: true }); // ✅ Only once

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ✅ Faster image cycle (4s instead of 6s, with smooth 0.8s fade)
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        setFade(false);
      }, 800);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Faster + smoother animation variants
  const headingVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, delay: 0.2, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, delay: 0.4, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="about-sprigstack-section py-md-5 py-0 pt-5 mx-3_5-0"
    >
      {/* Heading */}
      <div className="row mb-3 mx-0">
        <div className="col-12 px-3">
          <motion.h2
            className="about-heading"
            variants={headingVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            About Sprigstack
          </motion.h2>
        </div>
      </div>

      {/* Image + Text */}
      <div className="row align-items-center px-3">
        {/* Image Left */}
        {!isMobile && (
          <motion.div
            className="col-12 col-md-6 mb-4 mb-md-0"
            variants={imageVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="about-sprigstack-image-wrap">
              <LazyLoadImage
                effect="blur"
                src={images[currentIndex]}
                alt="Sprigstack Team"
                className={`about-sprigstack-image${fade ? " fade-out" : ""}`}
              />
            </div>
          </motion.div>
        )}

        {/* Text Right */}
        <motion.div
          className="col-12 col-md-6"
          variants={textVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="about-sprigstack-text">
            <p>
              Sprigstack is a leading software and web development company that
              provides services by focusing on cutting-edge technologies. In
              addition, we are experts at addressing client requirements for
              top-notch solutions in developing websites, web apps, mobile apps,
              UI/UX design, API development and integration, SEO, DevOps, and
              project management.
            </p>
            {isMobile && (
              <motion.div
                className="mb-4 mb-md-0"
                variants={imageVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                <div className="about-sprigstack-image-wrap">
                  <LazyLoadImage
                    effect="blur"
                    src={images[currentIndex]}
                    alt="Sprigstack Team"
                    className={`about-sprigstack-image${
                      fade ? " fade-out" : ""
                    }`}
                  />
                </div>
              </motion.div>
            )}
            <p>
              Since the initial stages, SprigStack has experienced significant
              growth and development. With over 9 years of experience in the
              software development industry, we consistently align ourselves
              with market trends, using our knowledge and skills to meet
              customer expectations.
            </p>
            <p>
              Among the top software development companies on{" "}
              <a
                href="https://clutch.co/profile/sprigstack"
                target="_blank"
                rel="noopener noreferrer"
              >
                Clutch
              </a>
              ,{" "}
              <a
                href="https://www.goodfirms.co/company/sprigstack"
                target="_blank"
                rel="noopener noreferrer"
              >
                Goodfirms
              </a>
              , and{" "}
              <a
                href="https://www.designrush.com/agency/profile/sprigstack"
                target="_blank"
                rel="noopener noreferrer"
              >
                Designrush
              </a>
              , we're proud of our well-earned reputation for delivering
              exceptional work. Continuously producing fruitful results to
              fulfil our client's expectations is the hallmark of our success.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSprigstack;
